/**
 * Customized layer styles. These are styles used throughout the app for
 * various components such as badges, buttons, dropdown menus, and boxes.
 *
 * Reference: https://chakra-ui.com/docs/features/text-and-layer-styles
 */

import { Statuses } from '../../../constants/statuses';

import colors from './colors';
import radii from './radii';

const LayerTypes = {
  Box: 'box',
  Status: 'status'
};

const StyledBoxes = {
  Accordion: 'accordion',
  Row: 'row',
  BgCard: 'bgCard',
  Container: 'container',
  TabPanel: 'tabPanel',
  PrimaryArchived: 'primaryArchived',
  SecondaryArchived: 'secondaryArchived',
  Panel: 'panel',
  PartialPanelTop: 'partialPanelTop',
  PartialPanelBottom: 'partialPanelBottom',
  ArchivedPanel: 'archivedPanel'
};

const StyledStatuses = { ...Statuses, All: 'all' };

export const layerStyles = {
  [StyledStatuses.Approved]: {
    background: colors.success.primary['50'],
    color: colors.success.primary['600']
  },
  [StyledStatuses.NotApproved]: {
    background: colors.error.primary['50'],
    color: colors.error.primary['600'],
    hoverBg: colors.error.primary['200']
  },
  [StyledStatuses.Expired]: {
    background: colors.error.primary['50'],
    color: colors.error.primary['600'],
    hoverBg: colors.error.primary['200']
  },
  [StyledStatuses.Archived]: {
    background: colors.greyScale['200'],
    color: colors.greyScale['700'],
    hoverBg: colors.greyScale['300']
  },
  [StyledStatuses.Active]: {
    background: colors.primaryScale['50'],
    color: colors.primaryScale['600'],
    hoverBg: colors.primaryScale['200']
  },
  [StyledBoxes.Accordion]: {
    bgColor: 'white',
    borderRadius: `${radii.md} ${radii.md} 0 0`,
    boxShadow: 'soft1'
  },
  [StyledBoxes.BgCard]: {
    bgColor: 'white',
    borderRadius: 'md',
    boxShadow: 'soft1'
  },
  [StyledBoxes.TabPanel]: {
    bgColor: 'white',
    borderRadius: '10px 10px 0px 0px',
    boxShadow: '0px 6px 18px rgba(123, 150, 178, 0.12)'
  },
  [StyledBoxes.Container]: {
    bgColor: 'transparent',
    boxShadow: 'none',
    borderRadius: 'none'
  },
  [StyledBoxes.Row]: {
    bgColor: 'white',
    color: colors.greyScale['900'],
    borderRadius: 'md'
  },
  [StyledBoxes.SecondaryArchived]: {
    bgColor: colors.background.secondaryArchived,
    color: colors.greyScale['800'],
    borderRadius: 'md'
  },
  [StyledBoxes.PrimaryArchived]: {
    bgColor: colors.background.primaryArchived,
    color: colors.greyScale['800'],
    borderRadius: 'md'
  },
  [StyledBoxes.Panel]: {
    borderRadius: 'lg',
    bgColor: 'white',
    color: colors.greyScale['900']
  },
  [StyledBoxes.PartialPanelTop]: {
    borderRadius: `${radii.lg} ${radii.lg} 0 0`,
    bgColor: 'white',
    color: colors.greyScale['900']
  },
  [StyledBoxes.PartialPanelBottom]: {
    borderRadius: `0 0 ${radii.lg} ${radii.lg}`,
    bgColor: 'white',
    color: colors.greyScale['900']
  },
  [StyledBoxes.ArchivedPanel]: {
    borderRadius: 'lg',
    bgColor: colors.background.primaryArchived,
    color: colors.greyScale['800']
  }
};

const LayerTypeStyles = {
  [LayerTypes.Status]: [
    StyledStatuses.Active,
    StyledStatuses.Approved,
    StyledStatuses.NotApproved,
    StyledStatuses.Archived,
    StyledStatuses.Expired,
    StyledStatuses.All
  ],
  [LayerTypes.Box]: [
    StyledBoxes.Accordion,
    StyledBoxes.BgCard,
    StyledBoxes.Row,
    StyledBoxes.TabPanel,
    StyledBoxes.SecondaryArchived,
    StyledBoxes.PrimaryArchived
  ]
};

export const getLayerStyle = (type, layerStyle) => {
  if (!LayerTypeStyles[type]) throw new Error(`layer type ${type} does not exist`);

  if (!LayerTypeStyles[type].includes(layerStyle)) {
    if (type === 'status') {
      return layerStyles.active;
    }

    throw new Error(`style ${layerStyle} does not exist for layer type ${type}`);
  }

  return layerStyles[layerStyle];
};

export default layerStyles;
